/**
 * Outil « Patrons qui se plient » (Boîte à outils, 6e → 4e).
 *
 * Plier : un patron (cube, pavé droit ; prisme droit et cylindre dès la 5e ;
 * pyramide et cône dès la 4e) se replie en solide, d'un geste ou avec le
 * curseur, et on tourne autour. Défi : construire les 11 patrons du cube
 * avec 6 carrés ; « Plier » montre ce que devient l'assemblage (deux faces qui
 * se superposent sont en rouge).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import {
  Note,
  PRIMARY_BUTTON,
  ParamSlider,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import {
  add,
  centroid,
  dot,
  foldNet,
  lerp3,
  norm,
  normal,
  normalize,
  scale,
  sub,
  type Camera,
  type NetFace,
  type V3,
} from '../../ui/space3d';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  FACE_STROKE,
  FACE_TONES,
  ORBIT_FOCUS_CLASS,
  OrbitButtons,
  pointsAttr,
  projector,
  useOrbit,
} from '../../ui/view3d';
import {
  SOLIDS,
  buildSolid,
  canonical,
  cellsOf,
  connected,
  cubeNets,
  cubeVerdict,
  solidFacts,
  squaresNet,
  type Cell,
  type Dims,
  type SolidId,
} from './solids';

const SIZE = 320;
const ERROR_TONE = '#f87171';

/* ---------- pliage animé ---------- */

function useFold() {
  const [t, setT] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);
  const animate = (target: 0 | 1) => {
    stop();
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setT(target);
      return;
    }
    timer.current = setInterval(() => {
      setT((v) => {
        const next = target === 1 ? Math.min(1, v + 0.04) : Math.max(0, v - 0.04);
        if (next === target) stop();
        return next;
      });
    }, 30);
  };
  const set = (v: number) => {
    stop();
    setT(v);
  };
  return { t, set, animate };
}

/* ---------- dessin ---------- */

interface Drawn {
  /** indice de la face dans le patron (clé stable après le tri) */
  idx: number;
  pts: Array<[number, number]>;
  depth: number;
  fill: string;
  opacity: number;
  shade: number;
  edges: Array<[[number, number], [number, number]]>;
}

interface Bounds {
  c0: V3;
  c1: V3;
  r0: number;
  r1: number;
}

/** cadrage du patron à plat (t = 0) et fermé (t = 1) : ne dépend que du patron */
function netBounds(net: readonly NetFace[]): Bounds {
  const flat = foldNet(net, 0).flat();
  const closed = foldNet(net, 1).flat();
  const c0 = centroid(flat);
  const c1 = centroid(closed);
  return {
    c0,
    c1,
    r0: Math.max(...flat.map((p) => norm(sub(p, c0)))),
    r1: Math.max(...closed.map((p) => norm(sub(p, c1)))),
  };
}

/**
 * Faces repliées vues par `cam`, de la plus lointaine à la plus proche.
 * Les charnières entre deux bandes d'une surface lisse ne se tracent pas.
 */
function drawNet(
  net: readonly NetFace[],
  { c0, c1, r0, r1 }: Bounds,
  t: number,
  cam: Camera,
  fills: readonly string[],
  smooth: ReadonlyArray<number | undefined>,
  opacity?: readonly number[],
): Drawn[] {
  const center = lerp3(c0, c1, t);
  const faces = foldNet(net, t);
  // à mi-pliage, une face relevée peut dépasser : on cadre sur la figure du moment
  const R = Math.max(r0 + (r1 - r0) * t, ...faces.flat().map((p) => norm(sub(p, center))));
  const proj = projector(cam, center, (SIZE / 2 - 12) / R, SIZE);
  // charnières « lisses » : ni tracées sur la fille ni sur la mère
  const skip = net.map(() => new Set<number>());
  const edgeIndex = (f: NetFace, a: readonly [number, number], b: readonly [number, number]) =>
    f.poly.findIndex((p, k) => {
      const q = f.poly[(k + 1) % f.poly.length]!;
      const same = (u: readonly [number, number], v: readonly [number, number]) =>
        Math.abs(u[0] - v[0]) < 1e-7 && Math.abs(u[1] - v[1]) < 1e-7;
      return (same(p, a) && same(q, b)) || (same(p, b) && same(q, a));
    });
  net.forEach((f, i) => {
    if (f.parent < 0 || !f.hinge) return;
    const s = smooth[i];
    if (s === undefined || s !== smooth[f.parent]) return;
    const [a, b] = f.hinge;
    skip[i]!.add(edgeIndex(f, a, b));
    skip[f.parent]!.add(edgeIndex(net[f.parent]!, a, b));
  });
  const light = normalize(
    add(add(scale(cam.toward, 0.8), scale(cam.up, 0.5)), scale(cam.right, -0.3)),
  );
  return faces
    .map((pts3, i) => {
      let n = normal(pts3);
      if (dot(n, cam.toward) < 0) n = scale(n, -1);
      const pts = pts3.map(proj);
      const edges: Drawn['edges'] = [];
      pts.forEach((p, k) => {
        if (!skip[i]!.has(k)) edges.push([p, pts[(k + 1) % pts.length]!]);
      });
      return {
        idx: i,
        pts,
        depth: cam.project(centroid(pts3)).z,
        fill: fills[i]!,
        opacity: opacity?.[i] ?? 1,
        shade: 0.45 + 0.55 * Math.max(0, dot(n, light)),
        edges,
      };
    })
    .sort((a, b) => a.depth - b.depth);
}

function FoldFigure({
  net,
  t,
  fills,
  smooth,
  opacity,
  label,
  orbit,
}: {
  net: readonly NetFace[];
  t: number;
  fills: readonly string[];
  smooth: ReadonlyArray<number | undefined>;
  /** opacité de chaque face (faces qui se superposent : les autres deviennent transparentes) */
  opacity?: readonly number[];
  label: string;
  orbit: ReturnType<typeof useOrbit>;
}) {
  const bounds = useMemo(() => netBounds(net), [net]);
  const drawn = drawNet(net, bounds, t, orbit.cam, fills, smooth, opacity);
  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label={label}
      className={cn(
        'border-ink-100 w-full max-w-[360px] cursor-grab rounded-2xl border',
        FIGURE_BG_CLASS,
        ORBIT_FOCUS_CLASS,
      )}
      {...orbit.svgProps}
    >
      {drawn.map((f) => (
        <g key={f.idx}>
          <polygon points={pointsAttr(f.pts)} fill={f.fill} fillOpacity={f.opacity} stroke="none" />
          <polygon
            points={pointsAttr(f.pts)}
            fill="#000"
            fillOpacity={(1 - f.shade) * 0.5 * f.opacity}
            stroke="none"
          />
          {f.edges.length > 0 && (
            <path
              d={f.edges
                .map(
                  ([a, b]) =>
                    `M${a[0].toFixed(1)},${a[1].toFixed(1)}L${b[0].toFixed(1)},${b[1].toFixed(1)}`,
                )
                .join('')}
              stroke={FACE_STROKE}
              strokeWidth={1.4}
              strokeLinejoin="round"
              fill="none"
            />
          )}
        </g>
      ))}
    </svg>
  );
}

const pct = (t: number) => `${Math.round(t * 100)} %`;

function FoldControls({
  fold,
  buttons = true,
}: {
  fold: ReturnType<typeof useFold>;
  buttons?: boolean;
}) {
  return (
    <div className="space-y-2">
      <ParamSlider
        name="Pliage"
        ariaLabel="Pliage"
        value={Math.round(fold.t * 100)}
        min={0}
        max={100}
        step={5}
        format={(v) => `${v} %`}
        onChange={(v) => fold.set(v / 100)}
      />
      {buttons && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={PRIMARY_BUTTON} onClick={() => fold.animate(1)}>
            Plier
          </button>
          <button type="button" className={SECONDARY_BUTTON} onClick={() => fold.animate(0)}>
            Déplier
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Plier ---------- */

function FoldMode({ rank }: { rank: number }) {
  const [solid, setSolid] = useState<SolidId>('cube');
  const [dims, setDims] = useState<Dims>({ L: 3, W: 2, H: 2 });
  const fold = useFold();
  const orbit = useOrbit({ yaw: -1.15, pitch: 0.8 });
  const built = useMemo(() => buildSolid(solid, dims), [solid, dims]);
  const fills = built.tones.map((k) => FACE_TONES[k % FACE_TONES.length]!);
  const facts = solidFacts(solid, dims);
  const name = SOLIDS.find((s) => s.id === solid)!.label;
  return (
    <div className="space-y-3">
      <div role="group" aria-label="Solide" className="flex flex-wrap gap-2">
        {SOLIDS.filter((s) => s.minRank <= rank).map((s) => (
          <ToggleChip
            key={s.id}
            on={s.id === solid}
            onClick={() => {
              setSolid(s.id);
              fold.set(0);
            }}
          >
            {s.label}
          </ToggleChip>
        ))}
      </div>
      {solid === 'cuboid' && (
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ['L', 'Longueur'],
              ['W', 'Largeur'],
              ['H', 'Hauteur'],
            ] as const
          ).map(([k, label]) => (
            <Stepper
              key={k}
              label={label}
              labelAbove
              value={dims[k]}
              min={1}
              max={4}
              onChange={(v) => setDims({ ...dims, [k]: v })}
            />
          ))}
        </div>
      )}
      <FoldFigure
        net={built.net}
        t={fold.t}
        fills={fills}
        smooth={built.smooth}
        label={`Patron : ${name.toLowerCase()}, plié à ${pct(fold.t)}`}
        orbit={orbit}
      />
      <OrbitButtons orbit={orbit} />
      <FoldControls fold={fold} />
      <Note live={false}>
        <span className="block font-semibold">{frenchSpaces(facts.title)}</span>
        {facts.facts.map((f) => (
          <span key={f} className="mt-1 block">
            {frenchSpaces(f)}
          </span>
        ))}
      </Note>
    </div>
  );
}

/* ---------- Défi : les 11 patrons du cube ---------- */

const GRID = 6;

function MiniNet({ form, dim = false }: { form: string; dim?: boolean }) {
  const cells = cellsOf(form);
  const w = Math.max(...cells.map((c) => c[0])) + 1;
  const h = Math.max(...cells.map((c) => c[1])) + 1;
  const u = 10;
  // le patron en escalier fait 2 × 5 : le cadre s'adapte à la plus grande dimension
  const n = Math.max(4, w, h);
  return (
    <svg viewBox={`-1 -1 ${n * u + 2} ${n * u + 2}`} className="h-12 w-12" aria-hidden>
      <g transform={`translate(${((n - w) * u) / 2},${((n - h) * u) / 2})`}>
        {cells.map(([x, y]) => (
          <rect
            key={`${x},${y}`}
            x={x * u}
            y={y * u}
            width={u}
            height={u}
            fill={dim ? 'var(--color-ink-200)' : FACE_TONES[0]}
            stroke={dim ? 'var(--color-ink-500)' : FACE_STROKE}
            strokeWidth={0.8}
          />
        ))}
      </g>
    </svg>
  );
}

type Verdict =
  | { kind: 'none' }
  | { kind: 'count'; n: number }
  | { kind: 'split' }
  | { kind: 'new'; found: number }
  | { kind: 'again' }
  | { kind: 'overlap'; block: boolean };

function hasBlock(cells: readonly Cell[]) {
  const s = new Set(cells.map((c) => `${c[0]},${c[1]}`));
  return cells.some(
    ([x, y]) => s.has(`${x + 1},${y}`) && s.has(`${x},${y + 1}`) && s.has(`${x + 1},${y + 1}`),
  );
}

function ChallengeMode() {
  const [cells, setCells] = useState<Cell[]>([]);
  const [found, setFound] = useState<string[]>([]);
  const [verdict, setVerdict] = useState<Verdict>({ kind: 'none' });
  const [shown, setShown] = useState(false);
  const [reveal, setReveal] = useState(false);
  // un seul arrêt de tabulation pour le quadrillage, les flèches s'y déplacent
  const [focusK, setFocusK] = useState(0);
  const cellRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const moveFocus = (k: number, key: string) => {
    const x = k % GRID;
    const y = Math.floor(k / GRID);
    const next =
      key === 'ArrowRight'
        ? y * GRID + Math.min(GRID - 1, x + 1)
        : key === 'ArrowLeft'
          ? y * GRID + Math.max(0, x - 1)
          : key === 'ArrowDown'
            ? Math.min(GRID - 1, y + 1) * GRID + x
            : key === 'ArrowUp'
              ? Math.max(0, y - 1) * GRID + x
              : null;
    if (next === null) return false;
    setFocusK(next);
    cellRefs.current[next]?.focus();
    return true;
  };
  const fold = useFold();
  const orbit = useOrbit({ yaw: -1.15, pitch: 0.85 });
  const all = useMemo(() => cubeNets(), []);

  const has = (x: number, y: number) => cells.some((c) => c[0] === x && c[1] === y);
  const toggle = (x: number, y: number) => {
    if (!has(x, y) && cells.length >= 6) {
      // rien ne change : la figure pliée reste
      setVerdict({ kind: 'count', n: 6 });
      return;
    }
    setShown(false);
    fold.set(0);
    if (has(x, y)) {
      setCells(cells.filter((c) => !(c[0] === x && c[1] === y)));
      setVerdict({ kind: 'none' });
      return;
    }
    setCells([...cells, [x, y]]);
    setVerdict({ kind: 'none' });
  };

  const check = useMemo(
    () => (cells.length === 6 && connected(cells) ? cubeVerdict(cells) : null),
    [cells],
  );
  const bad = new Set(
    shown && check && !check.ok
      ? check.overlaps.flat().map((i) => `${cells[i]![0]},${cells[i]![1]}`)
      : [],
  );

  const tryFold = () => {
    if (cells.length !== 6) {
      setVerdict({ kind: 'count', n: cells.length });
      return;
    }
    if (!connected(cells)) {
      setVerdict({ kind: 'split' });
      return;
    }
    setShown(true);
    fold.set(0);
    fold.animate(1);
    if (check?.ok) {
      const key = canonical(cells);
      if (found.includes(key)) setVerdict({ kind: 'again' });
      else {
        setFound([...found, key]);
        setVerdict({ kind: 'new', found: found.length + 1 });
      }
    } else setVerdict({ kind: 'overlap', block: hasBlock(cells) });
  };

  const sq = useMemo(
    () => (shown && cells.length === 6 ? squaresNet(cells) : null),
    [shown, cells],
  );
  const overlapIdx = new Set(check && !check.ok ? check.overlaps.flat() : []);

  const message =
    verdict.kind === 'count'
      ? verdict.n === 6 && cells.length === 6
        ? 'Un cube a 6 faces : 6 carrés, pas plus. Retire un carré pour en déplacer un.'
        : `Il faut exactement 6 carrés (tu en as ${verdict.n}).`
      : verdict.kind === 'split'
        ? 'Les 6 carrés doivent se toucher par un côté, sinon le patron tombe en morceaux.'
        : verdict.kind === 'new'
          ? `Bravo : c’est un patron du cube ! ${verdict.found} sur 11.`
          : verdict.kind === 'again'
            ? 'C’est bien un patron du cube, mais tu l’as déjà trouvé (tourné ou retourné, c’est le même).'
            : verdict.kind === 'overlap'
              ? `Une fois plié, deux carrés tombent sur la même face (en rouge) : ce n’est pas un patron du cube.${verdict.block ? ' Quatre carrés en carré 2 × 2 ne marchent jamais.' : ''}`
              : null;

  return (
    <div className="space-y-3">
      <p className="text-ink-600 text-sm">
        {frenchSpaces(
          'Touche 6 cases pour assembler 6 carrés, puis plie : est-ce un patron du cube ?',
        )}
      </p>
      <div
        role="group"
        aria-label="Quadrillage du patron"
        className="mx-auto grid w-fit gap-1"
        style={{ gridTemplateColumns: `repeat(${GRID}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: GRID * GRID }, (_, k) => {
          const x = k % GRID;
          const y = Math.floor(k / GRID);
          const on = has(x, y);
          const red = bad.has(`${x},${y}`);
          return (
            <button
              key={k}
              ref={(el) => {
                cellRefs.current[k] = el;
              }}
              type="button"
              tabIndex={k === focusK ? 0 : -1}
              aria-pressed={on}
              aria-label={`Case colonne ${x + 1}, ligne ${y + 1}${red ? ', superposée' : ''}`}
              onClick={() => toggle(x, y)}
              onFocus={() => setFocusK(k)}
              onKeyDown={(e) => {
                if (moveFocus(k, e.key)) e.preventDefault();
              }}
              className={cn(
                'h-11 w-11 rounded-md border transition-colors',
                on
                  ? red
                    ? 'border-error-600 bg-error-500'
                    : 'border-primary-500 bg-primary-300'
                  : 'border-ink-400 bg-ink-50 border-dashed',
              )}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={PRIMARY_BUTTON} onClick={tryFold}>
          Plier
        </button>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => {
            setCells([]);
            setShown(false);
            fold.set(0);
            setVerdict({ kind: 'none' });
          }}
        >
          Effacer
        </button>
      </div>
      <div aria-live="polite">
        {message && (
          <Note
            live={false}
            tone={
              verdict.kind === 'new' || verdict.kind === 'again'
                ? 'success'
                : verdict.kind === 'overlap'
                  ? 'error'
                  : 'neutral'
            }
          >
            {frenchSpaces(message)}
          </Note>
        )}
      </div>
      {sq && (
        <>
          <FoldFigure
            net={sq.net}
            t={fold.t}
            fills={sq.order.map((ci, k) =>
              overlapIdx.has(ci) ? ERROR_TONE : FACE_TONES[k % FACE_TONES.length]!,
            )}
            smooth={sq.net.map(() => undefined)}
            opacity={
              overlapIdx.size ? sq.order.map((ci) => (overlapIdx.has(ci) ? 0.95 : 0.4)) : undefined
            }
            label={`Tes 6 carrés, pliés à ${pct(fold.t)}`}
            orbit={orbit}
          />
          <OrbitButtons orbit={orbit} />
          <FoldControls fold={fold} buttons={false} />
        </>
      )}
      <div>
        <p className="text-ink-700 text-sm font-semibold">
          {frenchSpaces(`Patrons trouvés : ${found.length} sur 11`)}
        </p>
        <ul className="mt-2 grid grid-cols-6 gap-1" aria-label="Patrons du cube trouvés">
          {all.map((f) => {
            const ok = found.includes(f);
            return (
              <li
                key={f}
                className="border-ink-100 flex items-center justify-center rounded-lg border"
              >
                {ok || reveal ? (
                  <MiniNet form={f} dim={!ok} />
                ) : (
                  <span className="text-ink-300 flex h-12 w-12 items-center justify-center text-lg font-bold">
                    ?
                  </span>
                )}
                <span className="sr-only">
                  {ok ? 'trouvé' : reveal ? 'pas trouvé' : 'à trouver'}
                </span>
              </li>
            );
          })}
        </ul>
        {!reveal && found.length < 11 && (
          <button
            type="button"
            className={cn(SECONDARY_BUTTON, 'mt-2')}
            onClick={() => setReveal(true)}
          >
            Voir les 11 patrons
          </button>
        )}
      </div>
    </div>
  );
}

export function SolidNetsTool({ level }: { level?: string | null }) {
  const rank = Math.min(3, Math.max(1, schoolLevelRank(level) ?? 3));
  const [mode, setMode] = useState<'fold' | 'cube'>('fold');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'fold', label: 'Patrons des solides' },
          { value: 'cube', label: 'Les 11 patrons du cube' },
        ]}
        onChange={setMode}
      />
      {/* les deux modes restent montés : la progression du défi survit à un aller-retour */}
      <div hidden={mode !== 'fold'}>
        <FoldMode rank={rank} />
      </div>
      <div hidden={mode !== 'cube'}>
        <ChallengeMode />
      </div>
    </div>
  );
}
